<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useField, useForm } from 'vee-validate'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DMultiselect,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon,
  LockUnlocked03Icon,
  Lock03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getMasterConcepts,
  MasterConcepts,
  deleteMasterConcept,
  updateStateConceptsMaster,
  getListMasterConceptsGroups
} from '@/app/core/services/master-concepts'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref()
const typeAlertModal = ref('')
const acceptQuestion = ref()

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()

const itemsMasterConceptGroups = ref([] as any)
const { value: allowedGroups } = useField<string[]>(ref('allowedGroups'))
const searchGroups = ref('')

const tableMasterConcept = ref(null)
const conceptsMasterForDelete = ref()
const conceptsMasterUpdateState = ref()
const isDelete = ref(false)
const urlFile = ref(null)
const refresh = ref()
const payload = ref()


/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('masterConcepts.list.code'), table: 'code' },
  { name: 'name', title: t('masterConcepts.list.name'), table: 'name' },

  {
    name: 'state',
    title: t('masterConcepts.list.status'),
    table: 'is_inactive'
  },
  { name: 'format', title: t('masterConcepts.list.format'), table: 'format' },
  {
    name: 'category',
    title: t('masterConcepts.list.category'),
    table: 'category'
  },
  { name: 'type', title: t('masterConcepts.list.type'), table: 'type' },
  { name: 'groups', title: t('masterConcepts.list.groups'), table: 'groups' },
  {
    name: 'updated_at',
    title: t('masterConcepts.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<MasterConcepts[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createMasterConcept = () => {
  void router.push({ name: 'CreateMasterConcept' })
}
const updateMasterConcept = (id: any) => {
  void router.push({ name: 'UpdateMasterConcept', params: { id } })
}
const getMasterConcept = (id: any) => {
  void router.push({ name: 'GetMasterConcept', params: { id } })
}

/* Cambiar estado del maestro de concepto */
const changeStateManagement = (values: any) => {
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  conceptsMasterUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  titleModal.value = t('masterConcepts.list.actionStatus', {
    status: text,
    masterConcept: values.name as string
  })
  descriptionModal.value = ''
  typeAlertModal.value = 'question'
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  showModal.value = true
  isDelete.value = false
}
const changeStateConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await updateStateConceptsMaster(
    conceptsMasterUpdateState.value[0].id,
    conceptsMasterUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: !conceptsMasterUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('masterConcepts.notification.changeStatus', {
          masterConcept: conceptsMasterUpdateState.value[0].name as string,
          status: !conceptsMasterUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void conceptsMaster()
    })
    .catch((error: any) => {
      notificationError(error, 'masterConcepts', t, notifications, loading)
    })
}

/* Eliminar maestro de concepto */
const removeMasterConcept = (values: any) => {
  titleModal.value = t('masterConcepts.list.actionStatus', {
    status: t('table.action.delete'),
    masterConcept: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  typeAlertModal.value = 'question'
  showModal.value = true
  conceptsMasterForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteMasterConcept(conceptsMasterForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('masterConcepts.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void conceptsMaster()
    })
    .catch((error: any) => {
      notificationError(error, 'masterConcepts', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('.id')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('masterConcepts.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'masterConcepts', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

/* Listar maestros de concepto */
const conceptsMaster = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getMasterConcepts(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value,
      allowedGroups.value?.length > 0 ? allowedGroups.value : undefined
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'masterConcepts', t, notifications, loading)
  }
}

onMounted(async() => {
  itemsMasterConceptGroups.value = await getListMasterConceptsGroups()
})

</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="isDelete ? deleteConfirm() : changeStateConfirm()">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('masterConcepts.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('masterConcepts.list.description')" />
      </div>
      
      <div class="flex justify-between pt-8">
        <div class="flex mb-3 flex-auto flex-wrap gap-2 items-end">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-96"
            v-model="search"
            @keyup.enter="conceptsMaster" />          

            <div class="col-span-10 sm:col-span-5 md:col-span-2">            
              <DMultiselect           
                v-model="allowedGroups"
                :label="t('payrollConcepts.list.groups')"
                :headerTitle="t('payrollConcepts.list.groups')"
                :options="itemsMasterConceptGroups" />
            </div>

          <DBtn
            @click.prevent="conceptsMaster"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2 items-end justify-end">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createMasterConcept()"
            v-if="store.hasPermission('master_concept:create')">
            {{
              t('form.button.create', {
                form: t('masterConcepts.list.title', 0)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableMasterConcept"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), conceptsMaster()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), conceptsMaster()">
      <template #groups="data: any">
        <div class="p-2 text-gray-500 text-sm ">
         <ul>
          <li v-for="(group,index) in data.groups" :key="index"  class="list-disc">{{ group }}</li>
         </ul>
        </div>
      </template>
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('master_concept:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getMasterConcept(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              !data.is_inactive && store.hasPermission('master_concept:update')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateMasterConcept(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('master_concept:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('masterConcepts.list.activate')
                : t('masterConcepts.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateManagement(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              data.is_inactive && store.hasPermission('master_concept:delete')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeMasterConcept(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
